import styled from 'styled-components';
import useGetThemeCheckIn from '../../services/hooks/useGetThemeCheckIn';
import { useParams } from 'react-router-dom';
import { Empty, message, Spin } from 'antd';
import { useMemo, useState } from 'react';
import {
  IThemeCheckIn,
  IUpdateThemeCheckInBody,
  ThemeHomeCheckIn,
} from '../../services/types';
import colorTheme from 'constant/colors';
import checkInScreenshot from '../../assets/images/checkin-screenshot.png';
import googleNotShowReviewScreenshot from '../../assets/images/google-review-screenshot.png';
import googleShowReviewScreenshot from '../../assets/images/google-review-2-screenshot.png';
import marketingScreenshot from '../../assets/images/marketing-screeenshot.png';
import marketingNoCheckinScreenshot from '../../assets/images/marketing-no-checkin-screeenshot.png';
import checkinShopLogoThemeScreenshot from '../../assets/images/checkin-with-salon-logo.png';
import { IcChange } from 'components/Icon/icons';
import ChooseThemeCheckInModal, {
  useChooseThemeCheckInModalRef,
} from '../ChooseThemeCheckInModal';
import accountApis from 'services/accountApis';

export const THEME_OPTIONS = [
  {
    value: ThemeHomeCheckIn.CHECK_IN,
    label: 'Check-in',
    image: checkInScreenshot,
  },
  {
    value: ThemeHomeCheckIn.GOOGLE_NOT_SHOW_REVIEW,
    label: 'Google Not Show Review',
    image: googleNotShowReviewScreenshot,
  },
  {
    value: ThemeHomeCheckIn.GOOGLE_SHOW_REVIEW,
    label: 'Google Show Review',
    image: googleShowReviewScreenshot,
  },
  {
    value: ThemeHomeCheckIn.MARKETING,
    label: 'Marketing',
    image: marketingScreenshot,
  },
  {
    value: ThemeHomeCheckIn.MARKETING_NO_CHECKIN,
    label: 'Marketing No Check-in',
    image: marketingNoCheckinScreenshot,
  },
  {
    value: ThemeHomeCheckIn.CHECKIN_SHOP_LOGO_THEME,
    label: 'Check-in Shop Logo Theme',
    image: checkinShopLogoThemeScreenshot,
  },
];

const ThemeCheckIn = () => {
  const { id } = useParams();
  const { data, loading, refetch } = useGetThemeCheckIn(id || '');
  const useChooseThemeCheckInModa = useChooseThemeCheckInModalRef();
  const [selectedStation, setSelectedStation] = useState<number | null>(null);
  // Điều chỉnh tùy theo cấu trúc response thực tế của custom hook
  const stations: IThemeCheckIn[] = useMemo(() => {
    return [...(data || [])].sort(
      (a: IThemeCheckIn, b: IThemeCheckIn) => a.stationNumber - b.stationNumber,
    );
  }, [data]);

  const handleApplyTheme = async (theme: string) => {
    if (selectedStation === null) return;

    try {
      const payload: IUpdateThemeCheckInBody[] = (data || []).map(
        (station: IThemeCheckIn) => ({
          ...station,
          themeTemplate:
            station.stationNumber === selectedStation
              ? (theme as ThemeHomeCheckIn)
              : station.themeTemplate,
        }),
      );

      const res = await accountApis.updateThemeCheckIn(id || '', payload);

      if (res.status === 200) {
        message.success('Theme updated successfully');
        await refetch();
        setSelectedStation(null);
      } else {
        message.error('Failed to update theme');
      }
    } catch (error) {
      console.error('Error updating theme:', error);
      message.error('Error updating theme');
    }
  };

  return (
    <Container>
      <Header>
        <HeaderContent>
          <Title>Theme Check-in</Title>

          <Subtitle>Manage the check-in theme for each station.</Subtitle>
        </HeaderContent>
      </Header>
      <Spin spinning={!!loading}>
        {stations.length === 0 && !loading ? (
          <Empty description='No stations available' />
        ) : (
          <StationGrid>
            {stations.map((station) => {
              const theme = THEME_OPTIONS.find(
                (item) => item.value === station.themeTemplate,
              );

              return (
                <StationCard key={station.stationNumber}>
                  <CardHeader>
                    <div>
                      <StationTitle>
                        Station {station.stationNumber}
                      </StationTitle>

                      {/* <Tag color="blue">
                        {station.themeTemplate}
                      </Tag> */}
                    </div>

                    <ChangeButton
                      onClick={() => {
                        setSelectedStation(station.stationNumber);
                        useChooseThemeCheckInModa.current?.open(
                          station.themeTemplate,
                        );
                      }}
                    >
                      <IcChange fill={colorTheme.primary_active} />
                      Change Theme
                    </ChangeButton>
                  </CardHeader>

                  <PreviewContainer>
                    <PreviewImage src={theme?.image} alt={theme?.label} />
                  </PreviewContainer>
                </StationCard>
              );
            })}
          </StationGrid>
        )}
      </Spin>

      <ChooseThemeCheckInModal
        ref={useChooseThemeCheckInModa}
        onThemeChange={handleApplyTheme}
      />
    </Container>
  );
};

export default ThemeCheckIn;

const Container = styled.div`
  width: 100%;
  height: 100%;
  font-family: Poppins;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.h2`
  margin: 0;
  color: #1d2129;
  font-size: 28px;
  font-weight: 600;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #667085;
  font-size: 16px;
  line-height: 20px;
`;
const StationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const StationCard = styled.div`
  min-width: 0;
  padding: 16px;
  background: #fff;
  border: 1px solid #e8ebf0;
  border-radius: 12px;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 18px rgba(31, 41, 55, 0.06);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 18px;
`;

const StationTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  margin-right: 10px;
`;

const PreviewContainer = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border: 1px solid #e8ebf0;
  border-radius: 8px;
  background: #f3f5f8;
`;

const PreviewImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
`;

const ChangeButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 5px;
    font-size: 14px;
    font-weight: 600;
    font-family: Poppins;
    color: ${colorTheme.primary_active};
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
    background: #fff0f2;
    border: none;
    cursor: pointer;

    &:hover {
      background: #ffe5e7;
    }
  }
`;
